/**
 * API Client
 * Centralized Axios configuration with interceptors
 */

import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT, STORAGE_KEYS } from '../utils/constants';
import logger from '../utils/logger';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

const parseStoredToken = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const readTokenFromStorage = (storage) => {
  const authToken = parseStoredToken(storage.getItem('auth_token'));
  if (authToken) return authToken;
  return storage.getItem('token') || storage.getItem(STORAGE_KEYS.TOKEN);
};

const getToken = () => {
  // Prefer session token for current browser session, then persistent local token.
  return readTokenFromStorage(sessionStorage) || readTokenFromStorage(localStorage);
};

// Prevent duplicate in-flight mutation requests (double-click protection).
const inFlightMutations = new Map();

const safeSerialize = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof FormData !== 'undefined' && value instanceof FormData) return '[form-data]';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
};

const buildMutationKey = (method, url, data, config = {}) => {
  const paramsPart = safeSerialize(config.params);
  const dataPart = safeSerialize(data);
  return `${method.toUpperCase()}|${url}|${paramsPart}|${dataPart}`;
};

const executeMutation = (method, url, data, config = {}) => {
  if (config?.allowDuplicate === true) {
    return apiClient[method](url, data, config);
  }

  const requestConfig = { ...config };
  delete requestConfig.allowDuplicate;

  const key = buildMutationKey(method, url, data, requestConfig);
  const inFlight = inFlightMutations.get(key);
  if (inFlight) {
    logger.debug(`Duplicate ${method.toUpperCase()} blocked for ${url}`);
    return inFlight;
  }

  const requestPromise = (
    method === 'delete'
      ? apiClient.delete(url, { ...requestConfig, data })
      : apiClient[method](url, data, requestConfig)
  ).finally(() => {
    inFlightMutations.delete(key);
  });

  inFlightMutations.set(key, requestPromise);
  return requestPromise;
};

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add timestamp for logging
    config.metadata = { startTime: new Date() };

    // Skip Authorization header for Settings endpoints (permitAll in backend)
    const isSettingsEndpoint = config.url && config.url.includes('/Settings/');

    // Add Authorization token
    if (!isSettingsEndpoint) {
      const token = getToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        logger.debug(`Token present: ${token.substring(0, 20)}...`);
      } else {
        logger.warn(`No token found in storage for ${config.url}`);
      }
    }

    // Log API request in development
    logger.debug(`API ${config.method?.toUpperCase()} ${config.url}`);

    return config;
  },
  (error) => {
    logger.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log response time in development
    const duration = new Date() - response.config.metadata.startTime;
    logger.debug(`API ${response.config.method.toUpperCase()} ${response.config.url} completed in ${duration}ms`);

    return response;
  },
  (error) => {
    // Don't log canceled requests as errors
    if (error.code === 'ERR_CANCELED') {
      logger.debug('Request was canceled:', error.config?.url);
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      if (error.code === 'ERR_NETWORK') {
        logger.error('Network Error: Unable to connect to server');
      } else if (error.code === 'ECONNREFUSED') {
        logger.error('Connection Refused: Backend server is not accessible');
      } else {
        logger.error('Network Error:', error.message);
      }
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR'
      });
    }

    // Handle HTTP errors
    const { status, data } = error.response;

    if (status === 401) {
      // Unauthorized - clear token and redirect to login
      logger.error('Unauthorized: Token expired or invalid');

      // Clear all token storage locations
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token');
      sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('token');

      // Dispatch a custom event that AuthContext can listen to
      window.dispatchEvent(new CustomEvent('auth:unauthorized', {
        detail: { reason: 'Token expired or invalid' }
      }));

      // Redirect to app entry (login route) when unauthorized
      const basePath = process.env.REACT_APP_BASENAME || '/';
      const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;
      if (window.location.pathname !== normalizedBasePath) {
        window.location.href = normalizedBasePath;
      }
    } else if (status === 403) {
      logger.error('Forbidden:', error.config?.url);
    } else if (status === 404) {
      logger.error('Not Found:', error.config?.url);
    } else if (status >= 500) {
      logger.error('Server Error:', error.config?.url, data);
    }

    return Promise.reject({
      message: typeof data === 'string' ? data : (data?.message || 'An error occurred'),
      status,
      data
    });
  }
);

/**
 * Update Authorization header
 * @param {string} token - JWT token
 */
export const updateAuthHeader = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
  }
};

/**
 * API methods
 */
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data, config = {}) => executeMutation('post', url, data, config),
  put: (url, data, config = {}) => executeMutation('put', url, data, config),
  delete: (url, config = {}) => {
    const data = config?.data;
    return executeMutation('delete', url, data, config);
  },
  patch: (url, data, config = {}) => executeMutation('patch', url, data, config)
};

export default apiClient;

